import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { WarehouseCreateRequestSync, WarehouseWarehouseUpdateRequest } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { setWarehouseSyncCache } from './cache';
import { getWarehouseSync } from './queries';
import { parseWarehouseSync, type WarehouseSync } from './entity';

/** The create body: every export toggle plus exactly one storage config. */
export type WarehouseSyncCreateInput = WarehouseCreateRequestSync;

/**
 * The update body. A discriminated union on `update_type`: `settings` carries
 * the export toggles, each `*_config` variant carries that provider's
 * credentials, and `schema_version` carries the target version. The server
 * applies only the fields its branch reads.
 */
export type WarehouseSyncUpdateInput = WarehouseWarehouseUpdateRequest;

export interface CreateWarehouseSyncInput {
  workspaceId: number;
  sync: WarehouseSyncCreateInput;
}

export async function createWarehouseSync({
  workspaceId,
  sync,
}: CreateWarehouseSyncInput): Promise<WarehouseSync | null> {
  const data = await hydraUiApi.warehouse.createWarehouseSync({
    params: { path: { environment_id: String(workspaceId) } },
    body: { warehouse_sync: sync },
  });
  // The record is optional in the spec; the caller refetches rather than
  // parsing `undefined` into a Zod type error.
  return data.warehouse_sync == null ? null : parseWarehouseSync(data.warehouse_sync);
}

export interface UpdateWarehouseSyncInput {
  workspaceId: number;
  syncId: string;
  sync: WarehouseSyncUpdateInput;
}

export async function updateWarehouseSync({
  workspaceId,
  syncId,
  sync,
}: UpdateWarehouseSyncInput): Promise<WarehouseSync | null> {
  const data = await hydraUiApi.warehouse.updateWarehouseSync({
    params: { path: { environment_id: String(workspaceId), id: syncId } },
    body: { warehouse_sync: sync },
  });
  return data.warehouse_sync == null ? null : parseWarehouseSync(data.warehouse_sync);
}

export interface DeleteWarehouseSyncInput {
  workspaceId: number;
  syncId: string;
}

export async function deleteWarehouseSync({ workspaceId, syncId }: DeleteWarehouseSyncInput): Promise<void> {
  await hydraUiApi.warehouse.deleteWarehouseSync({
    params: { path: { environment_id: String(workspaceId), id: syncId } },
  });
}

export interface ValidateWarehouseConfigInput {
  workspaceId: number;
  sync: WarehouseSyncCreateInput;
}

/**
 * Connection test for a storage config, run before the sync is created. The
 * validators make live calls to S3/GCS/Yandex/Azure, so a rejection is a real
 * credential or connectivity failure and its body carries the per-provider
 * detail. Resolves with nothing on success.
 */
export async function validateWarehouseConfig({ workspaceId, sync }: ValidateWarehouseConfigInput): Promise<void> {
  await hydraUiApi.warehouse.validateWarehouseConfig({
    params: { path: { environment_id: String(workspaceId) } },
    body: { warehouse_sync: sync },
  });
}

/**
 * Cache the record the write returned, or — when the response omitted it, which
 * the spec allows — read it back.
 *
 * The read-back is deliberately not an invalidation. An invalidation refetches
 * active observers, and a GET that loses the read-after-write race answers 404,
 * which this layer maps to `null`: the workspace would read as "not connected"
 * moments after a successful write. So only a read that actually finds a record
 * is written to the cache. If it finds nothing, the slot is left as it was and
 * the next mount refetches it — `staleTime` is 0 on this query.
 */
async function writeOrReadBack(qc: QueryClient, workspaceId: number, sync: WarehouseSync | null): Promise<void> {
  if (sync != null) {
    setWarehouseSyncCache(qc, workspaceId, sync);
    return;
  }

  const refreshed = await getWarehouseSync(workspaceId).catch(() => null);
  if (refreshed != null) {
    setWarehouseSyncCache(qc, workspaceId, refreshed);
  }
}

type UseCreateWarehouseSyncVariables = Omit<CreateWarehouseSyncInput, 'workspaceId'>;

interface UseCreateWarehouseSyncOptions {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Not parsed from the URL: these hooks run during render, and a page can render under
   * `/workspaces/last` before the shell rewrites it.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<WarehouseSync | null, UseCreateWarehouseSyncVariables>;
}

/** @public */
export const useCreateWarehouseSync = ({ workspaceId, mutationConfig }: UseCreateWarehouseSyncOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateWarehouseSyncVariables) => createWarehouseSync({ workspaceId, ...variables }),
    onSuccess: (created, ...callbackArgs) => {
      // Set or invalidate, never both: an invalidation refetches active
      // observers immediately, and a GET that loses the read-after-write race
      // answers 404 — which this layer maps to `null`, flipping the workspace
      // back to "not connected" moments after it was connected.
      void writeOrReadBack(qc, workspaceId, created);
      onSuccess?.(created, ...callbackArgs);
    },
  });
};

type UseUpdateWarehouseSyncVariables = Omit<UpdateWarehouseSyncInput, 'workspaceId'>;

interface UseUpdateWarehouseSyncOptions {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Not parsed from the URL: these hooks run during render, and a page can render under
   * `/workspaces/last` before the shell rewrites it.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<WarehouseSync | null, UseUpdateWarehouseSyncVariables>;
}

/** @public */
export const useUpdateWarehouseSync = ({ workspaceId, mutationConfig }: UseUpdateWarehouseSyncOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (variables: UseUpdateWarehouseSyncVariables) => updateWarehouseSync({ workspaceId, ...variables }),
    onSuccess: (updated, ...callbackArgs) => {
      // The response is the whole record, and a `schema_version` update also
      // rewrites toggles the request never mentioned, so replace rather than merge.
      void writeOrReadBack(qc, workspaceId, updated);
      onSuccess?.(updated, ...callbackArgs);
    },
  });
};

type UseDeleteWarehouseSyncVariables = Omit<DeleteWarehouseSyncInput, 'workspaceId'>;

interface UseDeleteWarehouseSyncOptions {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Not parsed from the URL: these hooks run during render, and a page can render under
   * `/workspaces/last` before the shell rewrites it.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteWarehouseSyncVariables>;
}

/** @public */
export const useDeleteWarehouseSync = ({ workspaceId, mutationConfig }: UseDeleteWarehouseSyncOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteWarehouseSyncVariables) => deleteWarehouseSync({ workspaceId, ...variables }),
    onSuccess: (result, ...callbackArgs) => {
      // Disconnecting leaves the workspace with no sync, which is `null` here —
      // not an absent entry, which would read as "not loaded". Deliberately not
      // invalidated: a refetch that wins the race returns the pre-delete record
      // and resurrects the sync the user just disconnected.
      setWarehouseSyncCache(qc, workspaceId, null);
      onSuccess?.(result, ...callbackArgs);
    },
  });
};

type UseValidateWarehouseConfigVariables = Omit<ValidateWarehouseConfigInput, 'workspaceId'>;

interface UseValidateWarehouseConfigOptions {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Not parsed from the URL: these hooks run during render, and a page can render under
   * `/workspaces/last` before the shell rewrites it.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<void, UseValidateWarehouseConfigVariables>;
}

/** @public Validation touches no cached state, so it writes none. */
export const useValidateWarehouseConfig = ({ workspaceId, mutationConfig }: UseValidateWarehouseConfigOptions) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: (variables: UseValidateWarehouseConfigVariables) =>
      validateWarehouseConfig({ workspaceId, ...variables }),
  });
};
